import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Phone, RefreshCw, Loader2, Copy, Check, Clock, XCircle, CheckCircle, History, Plus, DollarSign, Ban } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Numbers() {
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [balance, setBalance] = useState(null);
  const [selectedService, setSelectedService] = useState('');
  const [activeVerification, setActiveVerification] = useState(null);
  const [verificationPolling, setVerificationPolling] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  
  // Ref to track if polling should stop
  const pollingCancelledRef = useRef(false);
  const [cancelling, setCancelling] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedNumber, setCopiedNumber] = useState(false);

  // Fetch services and balance on mount
  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch services and balance in parallel
      const [servicesRes, balanceRes] = await Promise.all([
        axios.get(`${API_URL}/numbers/services?number_type=mobile&reservation_type=verification`, { headers }).catch((err) => {
          console.error('Services fetch error:', err);
          return { data: { services: [] } };
        }),
        axios.get(`${API_URL}/numbers/balance`, { headers }).catch(() => ({ data: { balance: null } }))
      ]);

      const servicesList = servicesRes.data.services || [];
      setServices(servicesList);
      
      // Auto-select Instagram service if found
      const instagramService = servicesList.find(s => {
        const name = (s.name || s.serviceName || '').toLowerCase();
        return name.includes('instagram');
      });
      
      if (instagramService) {
        setSelectedService(instagramService.name || instagramService.serviceName);
      } else if (servicesList.length > 0) {
        setSelectedService(servicesList[0].name || servicesList[0].serviceName);
      }
      
      setBalance(balanceRes.data.balance);
    } catch (error) {
      console.error('Failed to load initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Create new verification
  const createVerification = async () => {
    if (!selectedService) {
      toast.error('Please select a service');
      return;
    }
    
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/numbers/verification?service_name=${encodeURIComponent(selectedService)}&capability=sms`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setActiveVerification({
          id: response.data.verification_id,
          phone_number: response.data.phone_number,
          status: response.data.status || 'pending',
          sms_code: null
        });
        toast.success('Number obtained! Waiting for SMS code...');
        startPolling(response.data.verification_id);
        // Refresh balance
        axios.get(`${API_URL}/numbers/balance`, { headers: { Authorization: `Bearer ${token}` } })
          .then(res => setBalance(res.data.balance))
          .catch(() => {});
      }
    } catch (error) {
      console.error('Failed to create verification:', error);
      toast.error(error.response?.data?.detail || 'Failed to get number');
    } finally {
      setCreating(false);
    }
  };

  // Poll for SMS code
  const startPolling = useCallback((verificationId) => {
    pollingCancelledRef.current = false;
    setVerificationPolling(true);
    
    const poll = async () => {
      // Check if polling was cancelled
      if (pollingCancelledRef.current) {
        setVerificationPolling(false);
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API_URL}/numbers/verification/${verificationId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Check again after API call
        if (pollingCancelledRef.current) {
          setVerificationPolling(false);
          return;
        }

        const data = response.data;
        
        // Stop polling if cancelled
        if (data.status === 'cancelled' || data.state === 'cancelled') {
          setVerificationPolling(false);
          return;
        }
        
        setActiveVerification(prev => {
          // Don't update if polling was cancelled
          if (pollingCancelledRef.current || !prev) return prev;
          return {
            ...prev,
            status: data.status || data.state,
            sms_code: data.sms_code,
            phone_number: data.phone_number || prev?.phone_number
          };
        });

        // Stop polling if we have a code or verification is complete
        // TextVerified uses 'verificationCompleted' state
        if (data.sms_code || data.state === 'verificationCompleted' || ['completed', 'timeout', 'reported'].includes(data.status)) {
          setVerificationPolling(false);
          if (data.sms_code) {
            toast.success('SMS code received!');
          }
          return;
        }

        // Continue polling
        setTimeout(() => poll(), 3000);
      } catch (error) {
        console.error('Polling error:', error);
        setVerificationPolling(false);
      }
    };

    poll();
  }, []);

  // Cancel verification
  const cancelVerification = async () => {
    if (!activeVerification?.id) return;
    
    setCancelling(true);
    
    // Stop polling immediately
    pollingCancelledRef.current = true;
    setVerificationPolling(false);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/numbers/verification/${activeVerification.id}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Verification cancelled');
      
      // Reset view
      setActiveVerification(null);
      
      // Refresh balance
      try {
        const balanceRes = await axios.get(`${API_URL}/numbers/balance`, { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        setBalance(balanceRes.data.balance);
      } catch (e) {
        console.error('Failed to refresh balance:', e);
      }
    } catch (error) {
      console.error('Cancel failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel');
      // Reset the polling flag if cancel failed
      pollingCancelledRef.current = false;
    } finally {
      setCancelling(false);
    }
  };

  // Fetch history
  const fetchHistory = async (page = 1) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/numbers/history?page=${page}&limit=5`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setHistory(response.data.history || []);
      setHistoryTotal(response.data.total || 0);
      setHistoryPage(page);
    } catch (error) {
      console.error('Failed to load history:', error);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    if (type === 'code') {
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } else {
      setCopiedNumber(true);
      setTimeout(() => setCopiedNumber(false), 2000);
    }
    toast.success('Copied to clipboard');
  };

  // Reset for new verification
  const resetVerification = () => {
    setActiveVerification(null);
    setVerificationPolling(false);
    fetchInitialData();
  };

  // Status badge component with better labels
  const StatusBadge = ({ status }) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock, label: 'Pending' },
      completed: { color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle, label: 'Used' },
      cancelled: { color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle, label: 'Cancelled' },
      timeout: { color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', icon: Clock, label: 'Expired' },
      reported: { color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: XCircle, label: 'Reported' },
      0: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock, label: 'Pending' },
      1: { color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle, label: 'Used' },
      2: { color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle, label: 'Cancelled' },
    };
    const config = statusConfig[status] || { color: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30', icon: Clock, label: String(status) };
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs border ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Numbers</h1>
          <p className="text-zinc-400 mt-1">Get one-time phone numbers for verification</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            className="border-zinc-700 text-white hover:bg-zinc-800"
            onClick={() => { fetchHistory(); setShowHistoryDialog(true); }}
          >
            <History className="w-4 h-4 mr-2" />
            History
          </Button>
        </div>
      </div>

      {/* Main Card */}
      <Card className="bg-zinc-900/50 border-zinc-800 max-w-2xl">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <Phone className="w-5 h-5" />
              Phone Number Verification
            </CardTitle>
            {/* Balance Display */}
            {balance !== null && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/10 border border-green-500/30 rounded-lg">
                <DollarSign className="w-4 h-4 text-green-400" />
                <span className="text-green-400 font-medium">{Number(balance).toFixed(2)}</span>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {!activeVerification ? (
            // Service selection and get number button
            <div className="space-y-4">
              {/* Service Dropdown */}
              <div className="space-y-2">
                <label className="text-sm text-zinc-400">Select Service</label>
                <Select value={selectedService} onValueChange={setSelectedService}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700 max-h-[300px]">
                    {services.length === 0 ? (
                      <SelectItem value="loading" disabled className="text-zinc-500">
                        Loading services...
                      </SelectItem>
                    ) : (
                      services.map((service, index) => {
                        const serviceName = service.name || service.serviceName;
                        return (
                          <SelectItem 
                            key={`${serviceName}-${index}`} 
                            value={serviceName}
                            className="text-white hover:bg-zinc-700"
                          >
                            {serviceName}
                          </SelectItem>
                        );
                      })
                    )}
                  </SelectContent>
                </Select>
                {services.length > 0 && (
                  <p className="text-xs text-zinc-500">{services.length} services available</p>
                )}
              </div>

              <Button 
                onClick={createVerification} 
                disabled={creating || !selectedService}
                className="w-full bg-white text-black hover:bg-zinc-200 h-12 text-base disabled:opacity-50"
              >
                {creating ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Getting Number...
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5 mr-2" />
                    Get New Number
                  </>
                )}
              </Button>
            </div>
          ) : (
            // Active verification display
            <div className="space-y-4">
              {/* Phone Number - Always visible */}
              <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm text-blue-400 font-medium">Phone Number to use</p>
                    <p className="text-2xl font-mono text-white mt-2 tracking-wide">
                      {activeVerification.phone_number || 'Loading number...'}
                    </p>
                    <p className="text-xs text-zinc-500 mt-2">Copy this number and enter it on Instagram</p>
                  </div>
                  {activeVerification.phone_number && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(activeVerification.phone_number, 'number')}
                      className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                    >
                      {copiedNumber ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                      {copiedNumber ? 'Copied!' : 'Copy'}
                    </Button>
                  )}
                </div>
              </div>

              {/* SMS Code Section - Separate from phone number */}
              <div className={`p-4 rounded-lg border ${activeVerification.sms_code ? 'bg-green-500/10 border-green-500/30' : 'bg-zinc-800/50 border-zinc-700'}`}>
                {activeVerification.sms_code ? (
                  // Code received
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-400 font-medium">SMS Code Received!</p>
                      <p className="text-4xl font-mono font-bold text-white mt-2 tracking-wider">
                        {activeVerification.sms_code}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(activeVerification.sms_code, 'code')}
                      className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                    >
                      {copiedCode ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                      {copiedCode ? 'Copied!' : 'Copy Code'}
                    </Button>
                  </div>
                ) : (
                  // Waiting for code
                  <div className="text-center py-4">
                    <div className="flex items-center justify-center gap-3 text-yellow-400">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span className="font-medium">Waiting for SMS code...</span>
                    </div>
                    <p className="text-xs text-zinc-500 mt-2">
                      The code will appear here automatically when received
                    </p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                {/* Cancel Button - show as long as no SMS code and not in final state */}
                {!activeVerification.sms_code && !['completed', 'cancelled', 'timeout', 'reported'].includes(activeVerification.status) && (
                  <Button
                    variant="outline"
                    onClick={cancelVerification}
                    disabled={cancelling}
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  >
                    {cancelling ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Ban className="w-4 h-4 mr-2" />
                    )}
                    Cancel Order
                  </Button>
                )}
                
                {/* Refresh button when not polling */}
                {!activeVerification.sms_code && !verificationPolling && !['completed', 'cancelled', 'timeout', 'reported'].includes(activeVerification.status) && (
                  <Button
                    variant="outline"
                    onClick={() => startPolling(activeVerification.id)}
                    className="border-zinc-700 text-white hover:bg-zinc-800"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                )}

                {/* Get New Number button when verification is done */}
                {(activeVerification.sms_code || ['completed', 'cancelled', 'timeout', 'reported'].includes(activeVerification.status)) && (
                  <Button
                    onClick={resetVerification}
                    className="bg-white text-black hover:bg-zinc-200"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Get New Number
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Instructions */}
      <Card className="bg-zinc-900/50 border-zinc-800 max-w-2xl">
        <CardHeader>
          <CardTitle className="text-white text-lg">How to use</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-zinc-400">
          <div className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center text-white text-xs">1</span>
            <p>Select the service (e.g., Instagram) and click "Get New Number"</p>
          </div>
          <div className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center text-white text-xs">2</span>
            <p>Copy the phone number and enter it on the service for verification</p>
          </div>
          <div className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center text-white text-xs">3</span>
            <p>Wait for the SMS code to appear here (usually within 1-2 minutes)</p>
          </div>
          <div className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center text-white text-xs">4</span>
            <p>Copy the SMS code and enter it on the service to complete verification</p>
          </div>
        </CardContent>
      </Card>

      {/* History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <History className="w-5 h-5" />
              Verification History
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {historyTotal} total verifications
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-3 pr-2">
            {history.length === 0 ? (
              <div className="text-center py-12 text-zinc-400">
                <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No verification history yet</p>
              </div>
            ) : (
              history.map((item, index) => (
                <div 
                  key={item.verification_id || index} 
                  className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-white">{item.phone_number || 'N/A'}</span>
                        <StatusBadge status={item.status} />
                      </div>
                      <p className="text-sm text-zinc-400">{item.service_name}</p>
                      {item.sms_code && (
                        <p className="text-sm text-green-400 font-mono">Code: {item.sms_code}</p>
                      )}
                    </div>
                    <div className="text-xs text-zinc-500">
                      {new Date(item.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          
          {historyTotal > 5 && (
            <div className="pt-4 border-t border-zinc-800 flex justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={historyPage <= 1}
                onClick={() => fetchHistory(historyPage - 1)}
                className="border-zinc-700 text-white"
              >
                Previous
              </Button>
              <span className="text-zinc-400 text-sm py-2">
                Page {historyPage} of {Math.ceil(historyTotal / 5)}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={historyPage >= Math.ceil(historyTotal / 5)}
                onClick={() => fetchHistory(historyPage + 1)}
                className="border-zinc-700 text-white"
              >
                Next
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
